import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { buttonVariants } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { DateRangeFilter } from "components/NewAdvancedSearch/filters/DateRangeFilter";
import { dateNavBounds } from "components/NewAdvancedSearch/filters/dateNavBounds";
import { RANGE_FILTER } from "constants/Constants";

const dateOps = [
  {
    symbol: "==",
    text: "Equals",
    value: RANGE_FILTER.EQUALS,
  },
  {
    symbol: ">",
    text: "After",
    value: RANGE_FILTER.GREATER_THAN,
  },
  {
    symbol: "<",
    text: "Before",
    value: RANGE_FILTER.LESS_THAN,
  },
  {
    symbol: "↔",
    text: "Between",
    value: RANGE_FILTER.BETWEEN,
  },
];

const initialValue = {
  op: RANGE_FILTER.GREATER_THAN,
  value: [],
};

function DateBoundPicker({ value, onSelect, placeholder }) {
  return (
    <Popover>
      <PopoverTrigger
        className={buttonVariants({
          variant: "outline",
          className:
            "tw:w-full tw:max-w-36 tw:justify-start tw:px-2.5 tw:font-normal tw:bg-white tw:dark:bg-background",
        })}
      >
        <CalendarIcon data-icon="inline-start" />
        {value ? format(value, "LLL dd, y") : <span>{placeholder}</span>}
      </PopoverTrigger>
      <PopoverContent className="tw:w-auto tw:p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onSelect}
          defaultMonth={value}
          captionLayout="dropdown"
          {...dateNavBounds()}
        />
      </PopoverContent>
    </Popover>
  );
}

export function DateFilter({ value, onChange }) {
  const current = value ?? initialValue;
  const bounds = current.value ?? [];
  const isBetween = current.op === RANGE_FILTER.BETWEEN;

  const handleChangeOp = op =>
    onChange({
      op,
      value: op === RANGE_FILTER.BETWEEN ? bounds : bounds.slice(0, 1),
    });

  const handleChangeBound = (index, next) => {
    const updated = [...bounds];
    updated[index] = next;
    onChange({ ...current, value: updated });
  };

  const handleChangeRange = range =>
    onChange({ ...current, value: [range?.from, range?.to] });

  const rangeValue =
    bounds[0] || bounds[1] ? { from: bounds[0], to: bounds[1] } : undefined;

  return (
    <div className="tw:flex tw:items-center tw:gap-4 tw:flex-1 tw:justify-end">
      <Select value={current.op} onValueChange={handleChangeOp}>
        <SelectTrigger
          aria-label="Comparison operator"
          className="tw:w-16 tw:bg-white tw:dark:bg-background"
        >
          <SelectValue placeholder="Op">
            {op => dateOps.find(d => d.value === op)?.symbol}
          </SelectValue>
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={false} className="tw:w-52">
          <SelectGroup>
            {dateOps.map(d => (
              <SelectItem key={d.value} value={d.value}>
                <span className="tw:inline-block tw:w-5 tw:text-center tw:font-medium">
                  {d.symbol}
                </span>
                {d.text}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {isBetween ? (
        <DateRangeFilter value={rangeValue} onChange={handleChangeRange} />
      ) : (
        <DateBoundPicker
          value={bounds[0]}
          onSelect={date => handleChangeBound(0, date)}
          placeholder="Pick a date"
        />
      )}
    </div>
  );
}
